/**
 * Shared constant for the "suppress auto-focus during canvas interaction"
 * behavior (Workplace post 1537926177964680).
 *
 * When the user has interacted with the canvas (pan/zoom/click/drag a node,
 * type in a prompt box, change a reference, etc.) within this window, automatic
 * viewport movement — the client-side run-state auto-focus AND the backend's
 * on-completion focus intent — is suppressed so we don't yank the viewport out
 * from under an active user. Explicit Director focus (`source === 'director'`)
 * bypasses this cooldown entirely.
 *
 * The interaction timestamp itself lives in a ref owned by WorkspacePageV2 and
 * is passed into useYCanvas, so it is scoped per workspace/canvas (NOT a module
 * global, which would leak across project switches).
 */
import { getTypedNodeData } from "../generated/nodeDataTypes";

/** Depth execution updates are data, never a request to navigate the canvas.
 * Identify the operation from the picked asset metadata, not its editable label.
 * The seeded source is marked pending; the finished output retains its source.
 */
export function isDepthVideoExecutionNode(node: {
  type?: string | null;
  data?: object;
}): boolean {
  if (node.type !== "video") return false;
  const data = getTypedNodeData(node, "video");
  const video = data?.out_video;
  const picked = video?.history?.[video.picked_index ?? 0];
  return (
    picked?.metadata?.mode === "depth_video_pending" ||
    picked?.result?.metadata?.source === "depth_video"
  );
}

export const CANVAS_AUTOFOCUS_COOLDOWN_MS = 8000;

/**
 * DOM attribute marking a subtree whose interactions should NOT count as canvas
 * interaction (the Director chatbox and its body-portaled popovers). The
 * document-level interaction listeners ignore events whose target is within an
 * element carrying this attribute.
 */
export const CANVAS_INTERACTION_IGNORE_ATTR = "data-canvas-interaction-ignore";
